/**
 * InstallRecord — the office's view of what the crew recorded on the phone
 * (Seth 2026-09-25: the job packet's Install Order + Rebate Test Form "need to
 * be a part of the install process"; refrigerant "easily tracked on the job and
 * inventory"). One card on the job's Work Order tab, under Coming Out:
 *
 *   · Refrigerant — each new system's charge (type, pounds added, total in the
 *     system, line set, the readings) and each old unit's recovery (the EPA
 *     record: type, pounds, which tank).
 *   · Install Checklist — the answers, a No first with the crew's note.
 *   · Rebate Test Form — whether this job needs it and why, the office's
 *     Automatic / Needed / Not Needed switch, and how far the crew got.
 *
 * Plus the two prints (the packet's Install Order and Rebate Test Form, filled
 * in) and the rows the Close the Job box shows — they warn, they never block.
 * Competitor frame: ServiceTitan shows completed forms on the job's Forms tab
 * and prints them as PDFs; nobody puts the refrigerant, the checklist and the
 * rebate paperwork in one place — the office checks one card before closing.
 */
import { useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, ClipboardCheck, FileText, Printer, Snowflake } from "lucide-react";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { useMemberName } from "@/components/TeamMemberSelect";
import { useJobEquipment, type Job } from "./jobs";
import { useRemovedEquipment } from "./installField";
import {
  useRefrigerantKeys, useJobRefrigerant, useRefrigerantTanks, useRefrigerantItems, refrigerantStatus, refrigerantCost, ozText,
  type RefrigerantLog,
} from "./refrigerant";
import { useInstallCheckItems, useJobChecks, checklistFor, checklistProgress, autoAnswers, ynnaLabel } from "./installChecklist";
import { useRebateTestStatus, useRebateTest, useSetRebateTestOverride, rebateCounts } from "./rebateTest";

const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
const who = (name: (v: string | null | undefined) => string | null, empno: string | null | undefined, email: string | null | undefined) =>
  name(empno) ?? (email ? email.split("@")[0] : null);

/** Everything the card and the close-out rows read — one set of queries (React Query dedupes). */
function useInstallRecord(jobId: string) {
  const { data: equip } = useJobEquipment(jobId);
  const { data: removed } = useRemovedEquipment(jobId);
  const { data: uses } = useRefrigerantKeys();
  const { data: logs } = useJobRefrigerant(jobId);
  const { data: items } = useInstallCheckItems();
  const { data: answers } = useJobChecks(jobId);
  const { data: rstatus } = useRebateTestStatus(jobId);
  const { data: rtest } = useRebateTest(jobId);
  return useMemo(() => {
    const units = (equip ?? []).filter((e) => e.kind !== "material");
    const rs = refrigerantStatus(equip ?? [], removed ?? [], logs ?? [], uses);
    const lines = checklistFor(items ?? [], units);
    const auto = autoAnswers(logs ?? []);
    const prog = checklistProgress(lines, answers, auto);
    const rebate = rstatus ?? null;
    const rc = rebateCounts(rtest?.data ?? {});
    return { equip: equip ?? [], removed: removed ?? [], logs: logs ?? [], rs, lines, auto, prog, answers, rebate, rtest: rtest ?? null, rc };
  }, [equip, removed, uses, logs, items, answers, rstatus, rtest]);
}

function Head({ icon: Icon, title, right }: { icon: typeof Snowflake; title: string; right?: React.ReactNode }) {
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-2">
      <Icon className="h-4 w-4 text-slate-400" />
      <h4 className="text-[13px] font-bold uppercase tracking-wide text-slate-500">{title}</h4>
      {right && <span className="ml-auto text-[13px]">{right}</span>}
    </div>
  );
}

export function InstallRecordSection({ job }: { job: Job }) {
  const r = useInstallRecord(job.id);
  const name = useMemberName();
  const { data: tanks } = useRefrigerantTanks();
  const { data: refItems } = useRefrigerantItems();
  const setOverride = useSetRebateTestOverride();
  const [showAll, setShowAll] = useState(false);

  const tankLabel = (id: string | null) => (id ? tanks?.find((t) => t.id === id)?.label ?? "a recovery tank" : null);
  const chargeFor = (unitId: string) => r.logs.find((l) => l.kind === "charge" && l.job_equipment_id === unitId) ?? null;
  const recoveryFor = (removedId: string) => r.logs.find((l) => l.kind === "recover" && l.removed_equipment_id === removedId) ?? null;
  const cost = refrigerantCost(r.logs, refItems);
  const rebateOn = !!r.rebate?.needed;
  const cur = job.rebate_test ?? null;

  const chargeLine = (l: RefrigerantLog) => {
    const bits = [
      l.lineset_ft != null ? `Line set ${l.lineset_ft} ft${l.factory_lineset_ft ? ` (the factory charge covers ${l.factory_lineset_ft})` : ""}` : null,
      l.metering === "txv" ? `TXV${l.subcool_actual != null ? ` · subcool ${l.subcool_actual}°${l.subcool_target != null ? ` (target ${l.subcool_target}°)` : ""}` : ""}`
        : l.metering === "piston" ? `Piston${l.superheat_actual != null ? ` · superheat ${l.superheat_actual}°${l.superheat_target != null ? ` (target ${l.superheat_target}°)` : ""}` : ""}` : null,
      l.vacuum_microns != null ? `${l.vacuum_microns} microns` : null,
      l.leak_test_psi != null ? `leak test ${l.leak_test_psi} psi` : null,
      l.brazed_by ? `soldered by ${l.brazed_by}` : null,
    ].filter(Boolean);
    return bits.join(" · ");
  };

  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-1 flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800">
        <ClipboardCheck className="h-[18px] w-[18px] text-brand-500" /> Install Record
        <InfoTip label="What this is">What the crew recorded on the phone — the refrigerant in and out, the Install Order checklist and the Rebate Test Form. It updates as they go. Nothing here blocks closing the job.</InfoTip>
        <span className="ml-auto flex flex-wrap gap-1.5">
          <a href={`/jobs/${job.id}/install-order`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">
            <Printer className="h-3.5 w-3.5" /> Install Order
          </a>
          {(rebateOn || r.rtest) && (
            <a href={`/jobs/${job.id}/rebate-test`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <Printer className="h-3.5 w-3.5" /> Rebate Test Form
            </a>
          )}
        </span>
      </h3>

      <div className="space-y-4">
        {/* ── Refrigerant ── */}
        <div>
          <Head icon={Snowflake} title="Refrigerant"
            right={r.rs.applies ? (r.rs.done ? <span className="font-semibold text-emerald-700">Logged</span> : <span className="font-semibold text-amber-700">Not finished</span>) : null} />
          {!r.rs.applies ? (
            <p className="text-sm text-slate-500">Nothing going in or coming out on this job holds refrigerant.</p>
          ) : (
            <div className="space-y-1.5">
              {r.rs.need.map((u) => {
                const l = chargeFor(u.id);
                const detail = l ? chargeLine(l) : "";
                return (
                  <div key={u.id} className="rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                      <span className="min-w-0">
                        <span className="font-semibold text-slate-800">{u.category || "New unit"}</span>
                        {(u.manufacturer || u.model) && <span className="text-slate-600"> · {[u.manufacturer, u.model].filter(Boolean).join(" ")}</span>}
                      </span>
                      {l ? (
                        <span className="text-slate-700">
                          <b className="font-semibold">{l.refrigerant}</b> · added <b className="font-semibold tabular-nums">{ozText(l.oz)}</b>
                          {l.total_oz != null && <> · <b className="font-semibold tabular-nums">{ozText(l.total_oz)}</b> in the system</>}
                        </span>
                      ) : (
                        <span className="font-medium text-amber-700">Charge not logged yet</span>
                      )}
                    </div>
                    {l && detail && <div className="mt-0.5 text-xs text-slate-500">{detail}</div>}
                    {l && <div className="mt-0.5 text-[11px] text-slate-400">Logged by {who(name, l.created_by_empno, l.created_by) ?? "the crew"} · {shortDate(l.updated_at ?? l.created_at)}</div>}
                  </div>
                );
              })}
              {r.rs.outs.map((o) => {
                const l = recoveryFor(o.id);
                return (
                  <div key={o.id} className="rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                      <span className="min-w-0">
                        <span className="font-semibold text-slate-800">Old {(o.category || "unit").toLowerCase()}</span>
                        {(o.brand || o.model) && <span className="text-slate-600"> · {[o.brand, o.model].filter(Boolean).join(" ")}</span>}
                      </span>
                      {l ? (
                        <span className="text-slate-700">
                          <b className="font-semibold">{l.refrigerant}</b> · {l.empty ? "it was empty (leaked out)" : <><b className="font-semibold tabular-nums">{ozText(l.oz)}</b> recovered{l.tank_id ? ` into ${tankLabel(l.tank_id)}` : ""}</>}
                        </span>
                      ) : (
                        <span className="font-medium text-amber-700">Recovery not logged yet</span>
                      )}
                    </div>
                    {l && <div className="mt-0.5 text-[11px] text-slate-400">Logged by {who(name, l.created_by_empno, l.created_by) ?? "the crew"} · {shortDate(l.created_at)}</div>}
                  </div>
                );
              })}
              {cost > 0.005 && (
                <p className="text-xs text-slate-500">
                  Refrigerant added cost <b className="font-semibold text-slate-700">{usd(cost)}</b> — it&apos;s in Job Costing, and the pounds come off stock when the job closes.
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Install checklist ── */}
        <div>
          <Head icon={ClipboardCheck} title="Install Checklist"
            right={r.lines.length > 0 ? (
              <span className={cn("font-semibold", r.prog.no.length ? "text-amber-700" : r.prog.missing.length ? "text-slate-500" : "text-emerald-700")}>
                {r.prog.answered} of {r.prog.total} answered{r.prog.no.length ? ` · ${r.prog.no.length} No` : ""}
              </span>
            ) : null} />
          {r.lines.length === 0 ? (
            <p className="text-sm text-slate-500">The checklist shows once equipment is on the job.</p>
          ) : (
            <div className="space-y-1.5 text-sm">
              {r.prog.no.map((l) => {
                const a = r.answers?.get(l.key);
                return (
                  <div key={l.key} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <span className="min-w-0">
                      <span className="font-semibold text-slate-800">No — {l.label}</span>
                      {a?.note ? <span className="text-slate-700">: “{a.note}”</span> : <span className="text-slate-500"> (no note)</span>}
                      {a && <span className="text-xs text-slate-400"> · {who(name, a.by_empno, a.by_email) ?? "the crew"}</span>}
                    </span>
                  </div>
                );
              })}
              {r.prog.missing.length > 0 && (
                <p className="text-slate-500">Not answered: {r.prog.missing.map((l) => l.label).join(" · ")}</p>
              )}
              {r.prog.answered > 0 && r.prog.no.length === 0 && r.prog.missing.length === 0 && (
                <p className="flex items-center gap-1.5 text-slate-600"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Every line answered, no No&apos;s.</p>
              )}
              <button type="button" onClick={() => setShowAll((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-900">
                {showAll ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {showAll ? "Hide the answers" : "Show every answer"}
              </button>
              {showAll && (
                <ol className="grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
                  {r.lines.map((l, i) => {
                    const v = r.prog.valueOf(l.key);
                    const fromAuto = !r.answers?.has(l.key) && !!r.auto.get(l.key);
                    return (
                      <li key={l.key} className="flex items-baseline gap-2 border-b border-slate-100 py-1">
                        <span className="w-5 shrink-0 text-right text-[11px] font-semibold tabular-nums text-slate-300">{i + 1}</span>
                        <span className="min-w-0 flex-1 text-slate-700">{l.label}</span>
                        <span className={cn("shrink-0 font-semibold", !v ? "text-slate-300" : v === "no" ? "text-amber-700" : "text-slate-800")}>
                          {v ? (l.kind === "ynna" ? ynnaLabel(v) : `${v}${l.unit ? ` ${l.unit}` : ""}`) : "—"}
                        </span>
                        {fromAuto && <span className="shrink-0 text-[10px] text-slate-400">from the charge</span>}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </div>

        {/* ── Rebate test form ── */}
        <div>
          <Head icon={FileText} title="Rebate Test Form"
            right={
              <span role="radiogroup" aria-label="Rebate Test Form for this job" className="inline-flex rounded-lg border border-hairline bg-slate-50 p-0.5">
                {([[null, "Automatic"], ["needed", "Needed"], ["not_needed", "Not Needed"]] as const).map(([v, label]) => (
                  <button key={label} type="button" role="radio" aria-checked={cur === v} disabled={setOverride.isPending}
                    onClick={() => { if (cur !== v) setOverride.mutate({ jobId: job.id, value: v }); }}
                    className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors",
                      cur === v ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-800")}>
                    {label}
                  </button>
                ))}
              </span>
            } />
          {!r.rebate ? (
            <p className="text-sm text-slate-400">Checking…</p>
          ) : (
            <div className="text-sm">
              <p className="text-slate-700">
                <b className={cn("font-semibold", rebateOn ? "text-slate-900" : "text-slate-500")}>{rebateOn ? "Needed" : "Not needed"}</b>
                <span className="text-slate-500"> — {r.rebate.reason}</span>
                {!rebateOn && cur == null && (
                  <InfoTip label="When the rebate test is needed" className="ml-1.5 align-middle">It turns on by itself when an AC, heat pump or mini-split goes in with a rebate from a utility that supplies electric (Settings → Quotes → Utility Rebates). Use Needed to add it anyway.</InfoTip>
                )}
              </p>
              {rebateOn && (
                r.rtest?.completed_at ? (
                  <p className="mt-1 flex items-center gap-1.5 text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Complete — {who(name, r.rtest.completed_by_empno, r.rtest.completed_by) ?? "the crew"} · {shortDate(r.rtest.completed_at)}. Print it and send it in with the rebate.
                  </p>
                ) : (
                  <p className="mt-1 text-slate-500">
                    {r.rtest ? `${r.rc.done} of ${r.rc.total} filled in so far` : "Not started"} — the crew fills it in on Wrap Up.
                  </p>
                )
              )}
              {setOverride.isError && <p className="mt-1 text-xs text-rose-600">Didn&apos;t save — try again.</p>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** The rows the Close the Job box shows for the crew's record — they warn, never block. */
export function InstallRecordCloseChecks({ jobId }: { jobId: string }) {
  const r = useInstallRecord(jobId);
  const rows: { ok: boolean; label: string }[] = [];
  if (r.rs.applies) {
    const miss = [...r.rs.chargeMissing.map((u) => u.category || "new unit"), ...r.rs.recoverMissing.map((o) => `old ${(o.category || "unit").toLowerCase()}`)];
    rows.push({ ok: r.rs.done, label: r.rs.done ? "Refrigerant logged" : `Refrigerant not logged: ${miss.join(", ")}` });
  }
  if (r.lines.length) {
    const ok = r.prog.missing.length === 0 && r.prog.no.length === 0;
    rows.push({
      ok,
      label: r.prog.no.length ? `Install checklist — No on ${r.prog.no.map((l) => l.label).join(", ")}`
        : r.prog.missing.length ? `Install checklist — ${r.prog.missing.length} line${r.prog.missing.length === 1 ? "" : "s"} not answered`
        : "Install checklist answered",
    });
  }
  if (r.rebate?.needed) {
    const done = !!r.rtest?.completed_at;
    rows.push({ ok: done, label: done ? "Rebate test form complete" : `Rebate test form — ${r.rtest ? `${r.rc.done} of ${r.rc.total} filled` : "not started"}` });
  }
  if (!rows.length) return null;
  return (
    <>
      {rows.map((x) => (
        <div key={x.label} className="flex items-center gap-1.5">
          {x.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
          <span className={x.ok ? "text-slate-600" : "text-slate-500"}>{x.label}</span>
        </div>
      ))}
      {rows.some((x) => !x.ok) && (
        <p className="flex items-center gap-1.5 pl-5 text-xs text-slate-500">Doesn&apos;t block the close
          <InfoTip label="Why this doesn't block closing">The crew&apos;s record is here so nothing gets missed (Installed → Install Record); an open line never stops the close.</InfoTip>
        </p>
      )}
    </>
  );
}
